import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../hooks/useNotification';
import { useSocket } from '../contexts/SocketContext';
import { useAudio } from '../contexts/AudioContext';
import { useAudioAlert } from '../hooks/useAudioAlert';
import { useTheme } from '../contexts/ThemeContext';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

import { format } from 'date-fns';
import missionService from '../services/missionService';
import MissionCard from '../components/MissionCard';
import MissionTable from '../components/MissionTable';
import LoadingSpinner from '../components/LoadingSpinner';
import { SegmentedControl } from '../components/SegmentedControl';
import { FilterPill } from '../components/FilterPill';
import { StatCard } from '../components/StatCard';
import { SearchInput } from '../components/SearchInput';
import {
  PlusIcon,
  ArrowRightOnRectangleIcon,
  BellIcon,
  BellSlashIcon,
  UserGroupIcon,
  ArrowPathIcon,
  MoonIcon,
  SunIcon,
  MapIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  XMarkIcon,
  TableCellsIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { Mission, Priority, MissionStatus, MissionType } from '../types';
import baseService from '../services/baseService';
import DisconnectedBanner from '../components/DisconnectedBanner';
import clsx from 'clsx';

const DispatchDashboard: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const { socket, connected } = useSocket();
  const { muted, toggleMute } = useAudio();
  const { playAlert, unlock } = useAudioAlert();
  const { darkMode, toggleDarkMode } = useTheme();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creatingMission, setCreatingMission] = useState(false);
  const [showShortcuts, setShowShortcuts] = useState(false);

  // View mode: 'active' excludes closed missions, 'all' shows everything
  const [viewMode, setViewMode] = useState<'active' | 'all'>('active');

  // Refinement filters (priority, type, search)
  const [filters, setFilters] = useState<{
    priority?: Priority[];
    missionType?: MissionType[];
    search?: string;
  }>({});

  const [statistics, setStatistics] = useState({
    activeMissions: 0,
    todayMissions: 0,
    completedToday: 0,
    criticalMissions: 0,
    urgentMissions: 0,
    routineMissions: 0
  });
  const [viewType, setViewType] = useState<'grid' | 'table'>('grid');
  const [sortBy, setSortBy] = useState<'time' | 'priority' | 'status' | 'base'>('time');
  const [totalMissionCount, setTotalMissionCount] = useState(0);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update current time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(timer);
  }, []);

  // Fetch missions
  const fetchMissions = useCallback(async () => {
    try {
      // Combine viewMode and filters into query params
      const queryFilters = {
        ...filters,
        viewMode,
      };

      const response = await missionService.getMissions(queryFilters);
      setMissions(response.missions);

      // Also fetch total count (all missions) for the badge
      // And get active missions for accurate priority counts
      let activeMissionsData = response.missions;
      if (viewMode === 'all' || Object.keys(filters).length > 0) {
        // If we're showing all missions or have filters, fetch active separately for counts
        const activeMissionsResponse = await missionService.getMissions({ viewMode: 'active' });
        activeMissionsData = activeMissionsResponse.missions;
      }

      if (viewMode === 'active') {
        const allMissionsResponse = await missionService.getMissions({ viewMode: 'all' });
        setTotalMissionCount(allMissionsResponse.missions.length);
      } else {
        setTotalMissionCount(response.missions.length);
      }

      // Calculate statistics from ACTIVE missions only (not filtered results)
      const active = activeMissionsData.filter(m =>
        m.status !== MissionStatus.MISSION_COMPLETE &&
        m.status !== MissionStatus.CANCELLED &&
        m.status !== MissionStatus.CLOSED
      ).length;

      const critical = activeMissionsData.filter(m =>
        m.priority === Priority.CRITICAL &&
        m.status !== MissionStatus.MISSION_COMPLETE &&
        m.status !== MissionStatus.CANCELLED &&
        m.status !== MissionStatus.CLOSED
      ).length;

      const urgent = activeMissionsData.filter(m =>
        m.priority === Priority.URGENT &&
        m.status !== MissionStatus.MISSION_COMPLETE &&
        m.status !== MissionStatus.CANCELLED &&
        m.status !== MissionStatus.CLOSED
      ).length;

      const routine = activeMissionsData.filter(m =>
        m.priority === Priority.ROUTINE &&
        m.status !== MissionStatus.MISSION_COMPLETE &&
        m.status !== MissionStatus.CANCELLED &&
        m.status !== MissionStatus.CLOSED
      ).length;

      const today = response.missions.filter(m => {
        const missionDate = m.timeReceived ? new Date(m.timeReceived) : new Date(m.dateCreated);
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        return missionDate >= todayStart;
      }).length;

      const completedToday = response.missions.filter(m =>
        m.status === MissionStatus.MISSION_COMPLETE &&
        m.timeMissionComplete &&
        new Date(m.timeMissionComplete).toDateString() === new Date().toDateString()
      ).length;

      setStatistics({
        activeMissions: active,
        todayMissions: today,
        completedToday,
        criticalMissions: critical,
        urgentMissions: urgent,
        routineMissions: routine
      });
    } catch (error: any) {
      console.error('Failed to fetch missions:', error);
      addNotification({
        type: 'error',
        title: 'Failed to fetch missions',
        message: error.response?.data?.error || error.message || 'Unknown error occurred'
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters, viewMode, addNotification]);

  // Initial load and filter changes
  useEffect(() => {
    fetchMissions();
  }, [fetchMissions]);

  // Socket listeners for real-time updates
  useEffect(() => {
    if (!socket) return;

    const handleMissionListUpdate = (data: any) => {
      // Refresh missions to update counts and filters
      fetchMissions();
      addNotification({
        type: 'info',
        title: 'Mission Updated',
        message: `Mission was updated by ${data.updatedBy}`
      });
    };

    const handleNewMission = (data: any) => {
      fetchMissions();
      const isCritical = data.mission?.priority === 'CRITICAL';
      playAlert(isCritical ? 'critical' : 'newMission', muted);
      addNotification({
        type: isCritical ? 'warning' : 'info',
        title: isCritical ? 'CRITICAL MISSION' : 'New Mission',
        message: `${isCritical ? '⚠️ ' : ''}Mission ${data.mission?.missionNumber || 'unknown'} received`
      });
    };

    socket.on('mission_list_updated', handleMissionListUpdate);
    socket.on('new_mission', handleNewMission);

    return () => {
      socket.off('mission_list_updated', handleMissionListUpdate);
      socket.off('new_mission', handleNewMission);
    };
  }, [socket, fetchMissions, addNotification, playAlert, muted]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchMissions();
  };

  // Create a mission immediately with sensible defaults and jump straight to
  // the mission form — dispatchers fill in details there (no modal).
  const handleCreateIncident = useCallback(async () => {
    if (creatingMission) return;

    try {
      setCreatingMission(true);

      const bases = await baseService.getActiveBases();
      const defaultBase =
        bases.find(b => b.name.toLowerCase().includes('squamish')) || bases[0];

      if (!defaultBase) {
        throw new Error('No active bases available — add a base in the Admin Panel first');
      }

      const newMission = await missionService.createMission({
        missionType: MissionType.SCENE_CALL,
        priority: Priority.ROUTINE,
        primaryBaseId: defaultBase.id,
      });

      addNotification({
        type: 'success',
        title: 'Mission Created',
        message: `Mission ${newMission.missionNumber} has been created successfully`
      });

      // Navigate to the new mission
      navigate(`/mission/${newMission.id}`);
    } catch (error: any) {
      addNotification({
        type: 'error',
        title: 'Failed to create mission',
        message: error.message
      });
    } finally {
      setCreatingMission(false);
    }
  }, [creatingMission, addNotification, navigate]);

  const togglePriority = (priority: Priority) => {
    const currentPriorities = filters.priority || [];
    const newPriorities = currentPriorities.includes(priority)
      ? currentPriorities.filter(p => p !== priority)
      : [...currentPriorities, priority];

    setFilters({
      ...filters,
      priority: newPriorities.length > 0 ? newPriorities : undefined
    });
  };

  const toggleMissionType = (type: MissionType) => {
    const currentTypes = filters.missionType || [];
    const newTypes = currentTypes.includes(type)
      ? currentTypes.filter(t => t !== type)
      : [...currentTypes, type];

    setFilters({
      ...filters,
      missionType: newTypes.length > 0 ? newTypes : undefined
    });
  };

  const handleSearch = (search: string) => {
    setFilters({ ...filters, search: search || undefined });
  };

  const clearFilters = () => {
    setFilters({});
  };

  const hasActiveFilters = !!(filters.priority || filters.missionType || filters.search);

  const sortedMissions = React.useMemo(() => {
    const sorted = [...missions];
    switch (sortBy) {
      case 'priority': {
        const priorityOrder = { CRITICAL: 0, URGENT: 1, ROUTINE: 2 };
        sorted.sort((a, b) => (priorityOrder[a.priority as keyof typeof priorityOrder] ?? 3) - (priorityOrder[b.priority as keyof typeof priorityOrder] ?? 3));
        break;
      }
      case 'status':
        sorted.sort((a, b) => a.status.localeCompare(b.status));
        break;
      case 'base':
        sorted.sort((a, b) => (a.primaryBase?.name || '').localeCompare(b.primaryBase?.name || ''));
        break;
      case 'time':
      default:
        sorted.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());
        break;
    }
    return sorted;
  }, [missions, sortBy]);

  const dashboardShortcuts = React.useMemo(() => [
    { key: 'n', ctrl: true, handler: () => handleCreateIncident(), description: 'New mission' },
    { key: 'r', ctrl: true, handler: () => handleRefresh(), description: 'Refresh' },
    { key: '?', handler: () => setShowShortcuts(s => !s), description: 'Show shortcuts' },
    { key: 'Escape', handler: () => { setShowShortcuts(false); }, description: 'Close overlay' },
  ], [handleCreateIncident]);

  useKeyboardShortcuts(dashboardShortcuts);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Disconnected Banner */}
      <DisconnectedBanner />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="bg-teaam-blue rounded-lg p-2 mr-4">
                <h1 className="text-lg font-bold text-white">TEAAM</h1>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-gray-900">Dispatch Dashboard</h2>
                <p className="text-sm text-gray-600">
                  {format(currentTime, 'EEEE, dd MMMM yyyy • HH:mm')} PT
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className={clsx(
                'flex items-center px-3 py-1 rounded-full text-sm',
                connected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              )}>
                <div className={clsx(
                  'w-2 h-2 rounded-full mr-2',
                  connected ? 'bg-green-400' : 'bg-red-400'
                )} />
                {connected ? 'Connected' : 'Disconnected'}
              </div>
              
              <button
                onClick={() => { unlock(); toggleMute(); }}
                className="p-2 text-gray-400 hover:text-gray-600"
                title={muted ? 'Enable notifications' : 'Mute notifications'}
              >
                {muted ? <BellSlashIcon className="h-5 w-5" /> : <BellIcon className="h-5 w-5" />}
              </button>
              
              <button
                onClick={toggleDarkMode}
                className="p-2 text-gray-400 hover:text-gray-600"
                title={darkMode ? 'Light mode' : 'Dark mode'}
              >
                {darkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>

              <button className="p-2 text-gray-400 hover:text-gray-600">
                <MapIcon className="h-5 w-5" />
              </button>

              <button
                onClick={() => navigate('/shift-handoff')}
                className="p-2 text-gray-400 hover:text-gray-600"
                title="Shift Handoff"
              >
                <ClipboardDocumentListIcon className="h-5 w-5" />
              </button>

              {(user?.role === 'ADMIN' || user?.role === 'SUPERVISOR') && (
                <button 
                  onClick={() => navigate('/admin')}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Admin Panel"
                >
                  <Cog6ToothIcon className="h-5 w-5" />
                </button>
              )}
              
              <div className="flex items-center space-x-2 pl-4 border-l">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">{user?.fullName}</p>
                  <p className="text-xs text-gray-500">{user?.role.replace(/_/g, ' ')}</p>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to log out?')) {
                      logout();
                    }
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600"
                  title="Logout"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Statistics Bar */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-200">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              label="Active Missions"
              value={statistics.activeMissions}
              icon="🚨"
              color="gray"
            />
            <StatCard
              label="Critical"
              value={statistics.criticalMissions}
              icon="🔴"
              color="red"
            />
            <StatCard
              label="Today's Missions"
              value={statistics.todayMissions}
              icon="📋"
              color="blue"
            />
            <StatCard
              label="Completed Today"
              value={statistics.completedToday}
              icon="✅"
              color="green"
            />
          </div>
        </div>
      </div>

      {/* View Mode Toggle & Filters */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="px-4 sm:px-6 lg:px-8 py-4">
          {/* View Mode Toggle & Action Button */}
          <div className="flex items-center justify-between mb-4">
            <SegmentedControl
              options={[
                { value: 'active', label: 'Active Missions', badge: statistics.activeMissions },
                { value: 'all', label: 'All Missions', badge: totalMissionCount }
              ]}
              value={viewMode}
              onChange={(value) => setViewMode(value as 'active' | 'all')}
            />

            <div className="flex items-center gap-3">
              {/* View Toggle */}
              <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
                <button
                  onClick={() => setViewType('grid')}
                  className={clsx(
                    'p-1.5 rounded-md transition-colors',
                    viewType === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  )}
                  title="Grid view"
                >
                  <Squares2X2Icon className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewType('table')}
                  className={clsx(
                    'p-1.5 rounded-md transition-colors',
                    viewType === 'table' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
                  )}
                  title="Table view"
                >
                  <TableCellsIcon className="h-4 w-4" />
                </button>
              </div>

              {/* Sort Control */}
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1.5 bg-white text-gray-700"
              >
                <option value="time">Sort: Time</option>
                <option value="priority">Sort: Priority</option>
                <option value="status">Sort: Status</option>
                <option value="base">Sort: Base</option>
              </select>

              <button
                onClick={handleRefresh}
                className={clsx(
                  'p-2 text-gray-400 hover:text-gray-600 transition-colors',
                  refreshing && 'opacity-50 cursor-not-allowed'
                )}
                disabled={refreshing}
                title="Refresh missions"
              >
                <ArrowPathIcon className={clsx('h-5 w-5', refreshing && 'animate-spin')} />
              </button>

              <button
                onClick={handleCreateIncident}
                className={clsx('btn btn-critical flex items-center', creatingMission && 'opacity-50 cursor-not-allowed')}
                data-testid="new-mission-button"
                disabled={creatingMission}
              >
                <PlusIcon className="h-5 w-5 sm:mr-2" />
                <span className="hidden sm:inline">{creatingMission ? 'CREATING...' : 'NEW MISSION'}</span>
              </button>
            </div>
          </div>

          {/* Refinement Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            {/* Priority Filters */}
            <div className="flex gap-2">
              <FilterPill
                label="Critical"
                count={statistics.criticalMissions}
                active={filters.priority?.includes(Priority.CRITICAL) || false}
                color="red"
                onClick={() => togglePriority(Priority.CRITICAL)}
              />
              <FilterPill
                label="Urgent"
                count={statistics.urgentMissions}
                active={filters.priority?.includes(Priority.URGENT) || false}
                color="yellow"
                onClick={() => togglePriority(Priority.URGENT)}
              />
              <FilterPill
                label="Routine"
                count={statistics.routineMissions}
                active={filters.priority?.includes(Priority.ROUTINE) || false}
                color="blue"
                onClick={() => togglePriority(Priority.ROUTINE)}
              />
            </div>

            <div className="h-6 w-px bg-gray-300" />

            {/* Mission Type Filters */}
            <div className="flex gap-2">
              <FilterPill
                icon="🚁"
                label="Scene Call"
                active={filters.missionType?.includes(MissionType.SCENE_CALL) || false}
                onClick={() => toggleMissionType(MissionType.SCENE_CALL)}
              />
              <FilterPill
                icon="🏥"
                label="Inter-Facility"
                active={filters.missionType?.includes(MissionType.INTER_FACILITY) || false}
                onClick={() => toggleMissionType(MissionType.INTER_FACILITY)}
              />
              <FilterPill
                icon="📞"
                label="Telehealth"
                active={filters.missionType?.includes(MissionType.TELEHEALTH) || false}
                onClick={() => toggleMissionType(MissionType.TELEHEALTH)}
              />
              <FilterPill
                icon="⏸"
                label="Standby"
                active={filters.missionType?.includes(MissionType.STANDBY) || false}
                onClick={() => toggleMissionType(MissionType.STANDBY)}
              />
            </div>

            <div className="flex-1" />

            {/* Search */}
            <SearchInput
              placeholder="Search missions..."
              value={filters.search || ''}
              onChange={handleSearch}
              className="w-80"
            />

            {/* Clear Filters */}
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1 transition-colors"
              >
                <XMarkIcon className="w-4 h-4" />
                Clear filters
              </button>
            )}
          </div>

          {/* Mission Count */}
          <div className="mt-3 text-sm text-gray-500">
            Showing {missions.length} mission{missions.length !== 1 ? 's' : ''}
            {viewMode === 'active' && <span className="text-blue-600 ml-1">(active only)</span>}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">

        {/* Mission Grid */}
        <div className="mt-6">
          {missions.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <UserGroupIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No missions found</h3>
              <p className="mt-1 text-sm text-gray-500">
                {filters.search || Object.keys(filters).length > 0
                  ? 'Try adjusting your filters or search criteria.'
                  : 'Get started by creating a new mission.'}
              </p>
              {Object.keys(filters).length === 0 && (
                <div className="mt-6">
                  <button
                    onClick={handleCreateIncident}
                    className="btn btn-primary"
                    disabled={creatingMission}
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    {creatingMission ? 'Creating...' : 'Create Mission'}
                  </button>
                </div>
              )}
            </div>
          ) : viewType === 'table' ? (
            <MissionTable missions={sortedMissions} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sortedMissions.map((mission) => (
                <MissionCard key={mission.id} mission={mission} />
              ))}
            </div>
          )}
        </div>
      </div>

      {showShortcuts && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowShortcuts(false)}>
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-4">Keyboard Shortcuts</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between"><span>New mission</span><kbd className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">Ctrl+N</kbd></div>
              <div className="flex justify-between"><span>Refresh</span><kbd className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">Ctrl+R</kbd></div>
              <div className="flex justify-between"><span>Show shortcuts</span><kbd className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">?</kbd></div>
              <div className="flex justify-between"><span>Close overlay</span><kbd className="px-2 py-0.5 bg-gray-100 rounded text-xs font-mono">Esc</kbd></div>
            </div>
            <button onClick={() => setShowShortcuts(false)} className="mt-4 btn btn-secondary w-full">Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DispatchDashboard;